import { useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { signIn, signOut, getCurrentUser } from '@/lib/auth';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Card } from '@/components/ui/Card';
import { ThemeToggle } from '@/components/ui/ThemeToggle';

const loginSchema = z.object({
  email: z.string().email('Invalid email address'),
  password: z.string().min(6, 'Password must be at least 6 characters'),
});

type LoginFormData = z.infer<typeof loginSchema>;

export default function Login() {
  const navigate = useNavigate();
  const [formError, setFormError] = useState<string>('');
  const [loading, setLoading] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  // Auth-aware redirect: if already logged in as admin, skip the form
  const currentUser = getCurrentUser();
  if (currentUser && currentUser.isadmin === true) {
    return <Navigate to="/dashboard" replace />;
  }

  const onSubmit = async (data: LoginFormData) => {
    setLoading(true);
    setFormError('');

    try {
      const { user, error, status } = await signIn(data.email, data.password);

      if (error) {
        if (status === 401) {
          setFormError('Email or password is incorrect.');
        } else if (status === 429) {
          setFormError('Too many login attempts. Wait a minute and try again.');
        } else if (status !== undefined && status >= 500) {
          setFormError('Server error. Try again in a moment.');
        } else if (status === undefined) {
          setFormError("Couldn't reach the server. Check your connection.");
        } else {
          setFormError(error);
        }
        setLoading(false);
        return;
      }

      if (!user?.isadmin) {
        await signOut();
        setFormError('This account is not authorized for the admin console.');
        setLoading(false);
        return;
      }

      navigate('/dashboard');
    } catch {
      setFormError("Couldn't reach the server. Check your connection.");
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full bg-surface flex items-center justify-center px-4 relative">
      <div className="absolute top-4 right-4">
        <ThemeToggle />
      </div>
      <Card glass className="w-full max-w-md p-8">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-6">
            <div className="flex flex-col items-center gap-1 mb-2">
              <h1 className="text-2xl font-bold text-text-primary tracking-tight">Rainmaker</h1>
              <span className="text-xs font-semibold text-primary-600 tracking-widest uppercase">Admin</span>
            </div>
          </div>
          <h2 className="text-2xl font-semibold text-text-primary">
            Welcome Back
          </h2>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
          {formError && (
            <div className="p-4 bg-error-50 dark:bg-error-900/20 border border-error-200 dark:border-error-800/30 rounded-md">
              <p className="text-sm text-error-700 dark:text-error-400">{formError}</p>
            </div>
          )}

          <Input
            {...register('email')}
            type="email"
            label="Email"
            placeholder="your@email.com"
            error={errors.email?.message}
          />

          <Input
            {...register('password')}
            type="password"
            label="Password"
            placeholder="••••••••"
            error={errors.password?.message}
          />

          <Button type="submit" variant="primary" className="w-full" disabled={loading}>
            {loading ? 'Logging in...' : 'Log In'}
          </Button>
        </form>

        <div className="mt-6 text-center text-sm">
          <p className="text-text-secondary">
            Need access? Contact your administrator.
          </p>
        </div>
      </Card>
    </div>
  );
}
